import { QueryKey, useMutation, UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { GraphQLClient } from "graphql-request";

import { graphql } from "lib/generated/app-service-gql";
import { GetCustomerDocumentUrlQuery } from "lib/generated/app-service-gql/graphql";
import { downloadFile } from "lib/utils";
import { useGraphQlClient, useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

export type BasicCustomerDocumentDetails = {
  id: string;
  fileName: string;
};

export function useDownloadCustomerDocument(
  options?: UseMutationOptions<void, unknown, BasicCustomerDocumentDetails, unknown>,
) {
  const { client } = useGraphQlClient();
  return useMutation<void, AxiosError, BasicCustomerDocumentDetails>(async ({ id, fileName }) => {
    const blob = await fetchCustomerDocumentBlob(id, client);

    // Download the file
    downloadFile(blob, fileName);
  }, options);
}

/**
 * Fetches the s3 url where a customer document is via an on demand query,
 * then downloads the file as a Blob.
 * @param documentId
 * @param client
 * @returns
 */
export async function fetchCustomerDocumentBlob(
  documentId: string,
  client: GraphQLClient,
): Promise<Blob> {
  const { getCustomerDocumentUrl } = await client.request(GET_CUSTOMER_DOCUMENTS_URL, {
    id: documentId,
  });

  const documentUrl = getCustomerDocumentUrl?.documentUrl;
  if (!documentUrl) throw new Error("Failed to retrieve document URL.");

  const response = await fetch(documentUrl);
  if (!response.ok) throw new Error("Failed to download document.");

  return await response.blob();
}

const GET_CUSTOMER_DOCUMENTS_URL = graphql(`
  query getCustomerDocumentUrl($id: String!) {
    getCustomerDocumentUrl(id: $id) {
      documentUrl
    }
  }
`);

export function useGetCustomerDocumentUrl(
  id: string,
  options?: UseQueryOptions<
    GetCustomerDocumentUrlQuery,
    unknown,
    GetCustomerDocumentUrlQuery,
    QueryKey
  >,
) {
  const { data, ...rest } = useGraphQlQuery(
    ["customerDocumentUrl", id],
    GET_CUSTOMER_DOCUMENTS_URL,
    [{ id }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
    },
  );

  return { data: data?.getCustomerDocumentUrl.documentUrl, ...rest };
}
